/**
 * Scroll the V2 shell <main> to top without touching window/document scroll.
 * window.scrollTo / scrollIntoView can pan the visual viewport on mobile and
 * push the persistent header + bottom tabs off-screen.
 */
export function scrollShellMainToTop() {
  if (typeof document === "undefined") return;
  const main = document.querySelector("[data-v2-shell-main]");
  if (main) {
    main.scrollTop = 0;
    return;
  }
  try {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  } catch {
    /* ignore */
  }
}

function shellMainIsScrollable(main) {
  if (!main || typeof window === "undefined") return false;
  const oy = window.getComputedStyle(main).overflowY;
  if (oy !== "auto" && oy !== "scroll" && oy !== "overlay") return false;
  return main.scrollHeight > main.clientHeight + 1;
}

function isTypingTarget(target) {
  if (!target || typeof target !== "object") return false;
  const tag = String(target.tagName || "").toLowerCase();
  if (tag === "input" || tag === "textarea" || tag === "select") return true;
  if (target.isContentEditable) return true;
  return Boolean(target.closest?.("[contenteditable='true']"));
}

/**
 * Document scroll is locked (dp-app-shell-lock); only <main> scrolls on
 * desktop Map/Inbox/etc. Arrow keys / PageUp / PageDown / Home / End would
 * otherwise do nothing while focus sits on a list button. Wire them to main.
 * Returns an unsubscribe function.
 */
export function bindShellMainKeyboardScroll() {
  if (typeof window === "undefined") return () => {};

  const onKeyDown = (event) => {
    if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) {
      return;
    }
    const key = event.key;
    const isArrow = key === "ArrowDown" || key === "ArrowUp";
    const isPage = key === "PageDown" || key === "PageUp";
    const isJump = key === "Home" || key === "End";
    // Space scrolls pages in browsers, but also activates focused buttons —
    // only treat it as scroll when focus is not on a control.
    const isSpace = key === " " || key === "Spacebar";
    if (!isArrow && !isPage && !isJump && !isSpace) return;

    if (isTypingTarget(event.target)) return;

    if (isSpace) {
      const t = event.target;
      const tag = String(t?.tagName || "").toLowerCase();
      if (
        tag === "button" ||
        tag === "a" ||
        tag === "summary" ||
        t?.getAttribute?.("role") === "button"
      ) {
        return;
      }
    }

    const main = document.querySelector("[data-v2-shell-main]");
    if (!shellMainIsScrollable(main)) return;

    const maxScroll = main.scrollHeight - main.clientHeight;
    const line = Math.max(32, Math.round(main.clientHeight * 0.08));
    const page = Math.max(line, Math.round(main.clientHeight * 0.9));

    let next = main.scrollTop;
    if (key === "ArrowDown") next += line;
    else if (key === "ArrowUp") next -= line;
    else if (key === "PageDown" || isSpace) next += page;
    else if (key === "PageUp") next -= page;
    else if (key === "Home") next = 0;
    else if (key === "End") next = maxScroll;

    next = Math.max(0, Math.min(maxScroll, next));
    if (next === main.scrollTop) return;

    event.preventDefault();
    main.scrollTop = next;
  };

  window.addEventListener("keydown", onKeyDown, { passive: false });
  return () => window.removeEventListener("keydown", onKeyDown);
}

function isOverflowScrollport(node) {
  if (!node || typeof window === "undefined") return false;
  const style = window.getComputedStyle(node);
  const oy = style.overflowY;
  if (oy !== "auto" && oy !== "scroll" && oy !== "overlay") return false;
  return node.scrollHeight > node.clientHeight + 1;
}

function revealElInScrollport(node, el, { offset = 8, align = "nearest" } = {}) {
  const nodeRect = node.getBoundingClientRect();
  const elRect = el.getBoundingClientRect();
  const pad = Math.max(0, Number(offset) || 0);
  const visibleTop = nodeRect.top + pad;
  const visibleBottom = nodeRect.bottom - pad;

  if (align === "start") {
    node.scrollTop += elRect.top - nodeRect.top - pad;
    return;
  }

  // nearest: only move when clipped; prefer keeping CTAs in view with padding.
  if (elRect.top >= visibleTop && elRect.bottom <= visibleBottom) return;
  if (elRect.bottom > visibleBottom) {
    node.scrollTop += elRect.bottom - visibleBottom;
  } else if (elRect.top < visibleTop) {
    node.scrollTop += elRect.top - visibleTop;
  }
}

/**
 * Scroll overflow ancestors so `el` is visible, without using
 * Element.scrollIntoView (which walks / pans the window on mobile).
 *
 * @param {Element|null|undefined} el
 * @param {{ offset?: number, align?: 'nearest'|'start' }} [options]
 *   - nearest (default): scroll only when clipped; reveal bottom when below fold
 *   - start: pin the top of `el` near the top of the scrollport
 */
export function scrollNearestScrollportToEl(
  el,
  { offset = 8, align = "nearest" } = {}
) {
  if (!el || typeof window === "undefined") return;
  const ports = [];
  let node = el.parentElement;
  while (node && node !== document.body) {
    if (isOverflowScrollport(node)) ports.push(node);
    node = node.parentElement;
  }
  // Innermost first, then outer — nested scroll areas (wizard pane + shell).
  for (const port of ports) {
    revealElInScrollport(port, el, { offset, align });
  }
}

/**
 * After layout (notes load, step change), bring a guided-demo CTA into view
 * if a scrollbar is hiding it.
 */
export function revealGuideAttentionTarget(el, { offset = 16 } = {}) {
  if (!el || typeof window === "undefined") return;
  scrollNearestScrollportToEl(el, { offset, align: "nearest" });
}
